import React, { createContext, useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-hot-toast';

export const AuthContext = createContext();

// ✅ Cognito Configuration from Environment Variables
const USER_POOL_ID = process.env.REACT_APP_USER_POOL_ID;
const COGNITO_CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID;
const CLIENT_SECRET = process.env.REACT_APP_COGNITO_CLIENT_SECRET;
const REGION = process.env.REACT_APP_REGION;

// ✅ Determine redirect URI based on environment
const REDIRECT_URI = process.env.NODE_ENV === 'production' 
  ? process.env.REACT_APP_REDIRECT_URI_PROD
  : process.env.REACT_APP_REDIRECT_URI_DEV;

const COGNITO_DOMAIN = process.env.REACT_APP_COGNITO_DOMAIN;
const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT;

// ✅ Add validation to catch missing env vars early
if (!COGNITO_CLIENT_ID || !CLIENT_SECRET || !COGNITO_DOMAIN || !API_ENDPOINT) {
  console.error('❌ Missing required environment variables!');
  console.error('Check your .env file and make sure all REACT_APP_* variables are set');
}

export function AuthProvider({ children }) {
  // ========== STATE ==========
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [organization, setOrganization] = useState(null);
  const [error, setError] = useState(null);
  const [authError, setAuthError] = useState(null);
  
  const tokenExchangeInProgress = useRef(false);

  // ========== HELPER FUNCTIONS (DEFINE EARLY) ==========
  const clearError = useCallback(() => {
    setAuthError(null);
    setError(null);
  }, []);

  // ========== MAIN FUNCTIONS ==========
  const refreshTokens = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const params = new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: COGNITO_CLIENT_ID,
        refresh_token: refreshToken
      });

      const basicAuth = btoa(`${COGNITO_CLIENT_ID}:${CLIENT_SECRET}`);

      const response = await fetch(`https://${COGNITO_DOMAIN}/oauth2/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Authorization': `Basic ${basicAuth}`
        },
        body: params.toString()
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const data = await response.json();
      
      localStorage.setItem('idToken', data.id_token);
      localStorage.setItem('accessToken', data.access_token);
      
      console.log('✅ Tokens refreshed successfully');
      
      return data.id_token;
    } catch (error) {
      console.error('Token refresh failed:', error);
      logout();
      throw error;
    }
  };

  const fetchUserOrganization = async (userId) => {
    try {
      console.log('Fetching organization for user:', userId);
      
      const idToken = localStorage.getItem('idToken');
      
      const response = await fetch(`${API_ENDPOINT}/get-user-organization?user_id=${userId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (!response.ok) {
        console.error('Failed to fetch organization:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('Error response:', errorText);
        return;
      }

      const responseData = await response.json();
      console.log('✅ Raw API response:', responseData);

      let orgData;
      if (responseData.statusCode && responseData.body) {
        console.log('📦 Detected Lambda proxy response format, parsing body string...');
        try {
          orgData = JSON.parse(responseData.body);
        } catch (parseError) {
          console.error('❌ Failed to parse response body:', parseError);
          console.error('Body content:', responseData.body);
          return;
        }
      } else {
        console.log('📦 Direct JSON response format');
        orgData = responseData;
      }
      
      console.log('✅ Parsed orgData:', orgData);
      console.log('✅ organization_id from response:', orgData.organization_id);
      console.log('✅ role from response:', orgData.role);
      console.log('✅ email from response:', orgData.email);
      
      if (!orgData.organization_id) {
        console.error('❌ organization_id is missing from API response!');
        console.error('Full response:', JSON.stringify(responseData, null, 2));
        return;
      }
      
      const organizationData = {
        organizationId: orgData.organization_id,
        organizationName: orgData.organization_name,
        userName: orgData.user_name,
        role: orgData.role,
        email: orgData.email
      };
      
      console.log('✅ Setting organization state:', organizationData);
      setOrganization(organizationData);
      
      localStorage.setItem('organizationId', orgData.organization_id);
      localStorage.setItem('organizationName', orgData.organization_name);
      localStorage.setItem('userName', orgData.user_name || '');
      localStorage.setItem('organizationRole', orgData.role);
      localStorage.setItem('organizationEmail', orgData.email);
      
      console.log('✅ Organization stored in localStorage:', orgData.organization_id);
      
    } catch (error) {
      console.error('❌ Error fetching organization:', error);
      console.error('Error stack:', error.stack);
    }
  };

  const getUserInfo = async (accessToken) => {
    try {
      const response = await fetch(`https://${COGNITO_DOMAIN}/oauth2/userInfo`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to get user info');
      }

      const userInfo = await response.json();
      console.log('User info retrieved:', userInfo);
      
      setUser({
        email: userInfo.email,
        username: userInfo.username,
        sub: userInfo.sub,
        ...userInfo
      });

      await fetchUserOrganization(userInfo.sub);
      
    } catch (error) {
      console.error('Error getting user info:', error);
      throw error;
    }
  };

  const exchangeCodeForTokens = async (code) => {
    try {
      const params = new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: COGNITO_CLIENT_ID,
        code: code,
        redirect_uri: REDIRECT_URI,
      });

      const basicAuth = btoa(`${COGNITO_CLIENT_ID}:${CLIENT_SECRET}`);

      const response = await fetch(`https://${COGNITO_DOMAIN}/oauth2/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Authorization': `Basic ${basicAuth}`,
        },
        body: params.toString(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Token exchange failed:', errorData);
        throw new Error('Failed to exchange code for tokens');
      }

      const data = await response.json();
      console.log('Tokens received successfully');

      localStorage.setItem('idToken', data.id_token);
      localStorage.setItem('accessToken', data.access_token);
      if (data.refresh_token) {
        localStorage.setItem('refreshToken', data.refresh_token);
      }

      const payload = data.id_token.split('.')[1];
      const decodedPayload = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
      const userId = decodedPayload.sub;

      console.log(`Fetching organization for user: ${userId}`);
      
      const orgResponse = await fetch(
        `${API_ENDPOINT}/get-user-organization?user_id=${userId}`,
        {
          headers: {
            'Authorization': `Bearer ${data.id_token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!orgResponse.ok) {
        const errorData = await orgResponse.json().catch(() => ({}));
        console.error('Failed to fetch organization:', errorData);
        throw new Error('Failed to fetch organization');
      }

      const orgResponseData = await orgResponse.json();
      let orgData;
      
      if (orgResponseData.statusCode === 200 && orgResponseData.body) {
        orgData = JSON.parse(orgResponseData.body);
      } else {
        orgData = orgResponseData;
      }

      if (!orgData.organization_id) {
        throw new Error('User is not associated with any organization');
      }

      const organizationData = {
        organizationId: orgData.organization_id,
        organizationName: orgData.organization_name,
        userName: orgData.user_name,
        role: orgData.role,
        email: orgData.email
      };

      setOrganization(organizationData);

      localStorage.setItem('organizationId', orgData.organization_id);
      localStorage.setItem('organizationName', orgData.organization_name);
      localStorage.setItem('userName', orgData.user_name || '');
      localStorage.setItem('organizationRole', orgData.role);
      localStorage.setItem('organizationEmail', orgData.email);

      setUser({
        sub: userId,
        email: decodedPayload.email,
        username: decodedPayload['cognito:username']
      });

    } catch (error) {
      console.error('❌ Error during token exchange:', error);
      
      let errorMessage = 'Login failed. Please try again.';
      
      if (error.message.includes('Failed to exchange code')) {
        errorMessage = 'Authentication failed. Please check your credentials.';
      } else if (error.message.includes('Failed to fetch organization')) {
        errorMessage = 'Could not load your organization. Please contact support.';
      } else if (error.message.includes('not associated with any organization')) {
        errorMessage = 'Your account is not linked to an organization. Please contact support.';
      }
      
      setAuthError(errorMessage);
      toast.error(errorMessage);
      
      setUser(null);
      setOrganization(null);
      
      localStorage.removeItem('idToken');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('organizationId');
      localStorage.removeItem('organizationName');
      localStorage.removeItem('userName');
      localStorage.removeItem('organizationRole');
      localStorage.removeItem('organizationEmail');
    }
  };

  const checkUser = async () => {
    if (tokenExchangeInProgress.current) {
      console.log('checkUser already running, skipping...');
      return;
    }
    
    tokenExchangeInProgress.current = true;
    
    try {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      
      if (code) {
        console.log('Authorization code received, exchanging for tokens...');
        await exchangeCodeForTokens(code);
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }

      const accessToken = localStorage.getItem('accessToken');
      const idToken = localStorage.getItem('idToken');
      
      if (accessToken && idToken) {
        console.log('Found existing tokens, fetching user info...');
        
        const storedOrgId = localStorage.getItem('organizationId');
        const storedRole = localStorage.getItem('organizationRole');
        const storedEmail = localStorage.getItem('organizationEmail');
        
        if (storedOrgId) {
          console.log('✅ Restoring organization from localStorage:', storedOrgId);
          setOrganization({
            organizationId: storedOrgId,
            organizationName: localStorage.getItem('organizationName') || '',
            userName: localStorage.getItem('userName') || '',
            role: storedRole || 'user',
            email: storedEmail
          });
        }
        
        await getUserInfo(accessToken);
      }
    } catch (error) {
      console.error('Error checking user:', error);
      localStorage.removeItem('accessToken');
      localStorage.removeItem('idToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('organizationId');
      localStorage.removeItem('organizationName');
      localStorage.removeItem('userName');
      localStorage.removeItem('organizationRole');
      localStorage.removeItem('organizationEmail');
    } finally {
      setLoading(false);
      tokenExchangeInProgress.current = false;
    }
  };

  const login = () => {
    try {
      clearError();
      console.log('🔐 Initiating login...');
      const authUrl = `https://${COGNITO_DOMAIN}/oauth2/authorize?client_id=${COGNITO_CLIENT_ID}&response_type=code&scope=email+openid+profile&redirect_uri=${encodeURIComponent(REDIRECT_URI)}`;
      console.log('🔐 Redirecting to Cognito...');
      window.location.href = authUrl;
    } catch (error) {
      console.error('❌ Login error:', error);
      setAuthError('Failed to initiate login. Please try again.');
      toast.error('Failed to initiate login. Please try again.');
    }
  };

  const logout = () => {
    localStorage.removeItem('idToken');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('organizationId');
    localStorage.removeItem('organizationName');
    localStorage.removeItem('userName');
    localStorage.removeItem('organizationRole');
    localStorage.removeItem('organizationEmail');
    
    setUser(null);
    setOrganization(null);
    clearError();
    
    window.location.href = `https://${COGNITO_DOMAIN}/logout?client_id=${COGNITO_CLIENT_ID}&logout_uri=${encodeURIComponent(REDIRECT_URI)}`;
  };

  // ========== EFFECTS ==========
  useEffect(() => {
    let isCancelled = false;
    
    const initAuth = async () => {
      if (!isCancelled) {
        await checkUser();
      }
    };
    
    initAuth();
    
    return () => {
      isCancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ========== PROVIDER ==========
  return (
    <AuthContext.Provider value={{ 
      user, 
      organization, 
      loading,
      error,
      authError, 
      login, 
      logout,
      clearError,
      refreshTokens, // ✅ Expose for use in api.js
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
}